import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Button, Message, Space } from '@bedrock/components';
import { showAlertDebounce } from '@mpc/common/src/utils';
import { getAttachementUploadUrl } from 'containers/OutWorkbench/utils';
import _ from 'lodash';
import { TabContent } from 'containers/SmWorkbench/ProjectDetail/Show/Detail/Bottom/components';
import './index.less';
import NewCooperate from "@mpc/common/src/features/NewCooperate";
import StatementAttachmentItem from '@mpc/common/src/features/StatementAttachmentItem';
import { useMemoizedFn, useRequest} from "ahooks";
import UploaderMarketingWorks from "containers/OutWorkbench/components/UploaderMarketingWorks";
import UploadHeatingBoostWorks from 'containers/OutWorkbench/components/UploadHeatingBoostWorks';
import QuotationSummary, { SubmitCaseReportPopover } from '@mpc/common/src/features/QuotationSummary';
import { Check, RollTopLeft } from '@bedrock/icons-react';
import { demandMoveMove, outsourceDemandMoveMove, outsourceDemandHideInvoice } from '@/services/api';
import { SubmitModal } from '@mpc/common/src/components/SubmitModal';

export const XiaoHao = (props: any) => {
  const {
    detail,
    tabKey,
    projectMove,
    loadDetail,
    menuKey = 'making',
    handleSubmitBody,
    // 上插槽
    topSlot = undefined,
  } = props;

  const [loading, setLoading] = useState(false);
  const cooperateRef = useRef<any>(null);
  const quotationSummaryRef = useRef<any>(null);
  const statementAttachmentRef = useRef<any>(null);

  const [cooperateData, setCooperateData] = useState<any>(null);
  const [overseasAttachment, setOverseasAttachment] = useState<any>({});
  /** QuotationSummary 内部表单值，用于监听子组件变化（如参与 useRequest 等） */
  const [summaryValues, setSummaryValues] = useState<any>(null);

  useEffect(() => {
    setOverseasAttachment({
      signed_statement_attachs: detail?.signed_statement_attachs
    });
  }, [detail]);

  const { data: isHideInvoice } = useRequest(async () => {
    const res = await outsourceDemandHideInvoice({ demand_id: detail.id, settle_price: summaryValues?.$final_settle_price?.value })
    return res?.data
  }, {
    ready: summaryValues?.$final_settle_price?.value,
    refreshDeps: [summaryValues?.$final_settle_price?.value],
    debounceWait: 300,
  })

  const handleSubmit = useMemoizedFn(async () => {
    if (cooperateRef?.current?.valid() === false) {
      return;
    }

    let summaryValues: any = {};
    try {
      summaryValues = await quotationSummaryRef.current.validateFields();
    } catch (e) {
      return;
    }

    if (detail?.product_area === 'OVERSEAS') {
      try {
        await statementAttachmentRef?.current?.validateFields?.();
      } catch (e) {
        return;
      }
    }

    // 处理结案报告附件
    const settle_report_attachs = summaryValues?.settle_report_attachs_blob_ids || [];
    const settle_report_attachs_blob_ids = settle_report_attachs.map((file: any) => {
       // 兼容回显数据
       if (file?.blob) {
         return {
           blob_id: file?.blob?.id,
           blob_key: file?.blob?.key
         };
       }
       return null;
    }).filter(Boolean);

    // 结算单附件
    const signed_statement_attachs = summaryValues?.signed_statement_attachs || [];
    const signed_statement_attachs_blob_keys = signed_statement_attachs.map((item: any) => item?.blob?.key).filter(Boolean);

    // 海外版发票附件处理逻辑
    // 海外版：使用 overseasAttachment 中的数据，并移除 signed_statement_attachs 字段
    // 国内版：仅需确保 signed_statement_attachs 为 undefined，具体附件通过 signed_statement_attachs_blob_keys 传递
    const invoice_upload_dto = detail?.product_area === 'OVERSEAS' ? {
        ...overseasAttachment,
        signed_statement_attachs: undefined
    } : {
        signed_statement_attachs: undefined
    }; 
    
    let submitBody: any = {
      id: detail.id,
      making: {
        demand_advertising_attributes: {
          id: detail?.demand_advertising?.id,
          final_settle_price: summaryValues['final_settle_price'],
          final_settle_currency: detail?.demand_advertising?.dspf_offer_currency,
        },
        invoice_upload_dto, 
        signed_statement_attachs_blob_keys: (detail?.product_area === 'OVERSEAS') 
            ? overseasAttachment?.signed_statement_attachs?.map?.((item: any) => item?.blob?.key) || []
            : signed_statement_attachs_blob_keys,
            
        settle_report_attachs_blob_ids,
        is_pass: true,
        ...(cooperateData || {}),
        
        // 注意：加热助推(boost) 和 作品上传(upload_work) 的数据不需要在此处显式提交
        // 它们由各自的组件 (UploadHeatingBoostWorks, UploaderMarketingWorks) 内部处理或仅用于前端校验
      },
    };

    if (handleSubmitBody) {
      submitBody = handleSubmitBody(submitBody);
    }

    setLoading(true);
    return projectMove(submitBody, { menuKey })
      .then((res: any) => {
        showAlertDebounce({ type: 'success', message: res.res_msg });
        loadDetail();
      })
      .finally(() => {
        setLoading(false);
      });
  });

  const handleBack = useMemoizedFn(async () => {
    await outsourceDemandMoveMove({
      id: detail.id,
      state: detail.state,
      pass: false,
      note: '',
      form: {}
    });
    Message.success('操作成功');
    loadDetail(detail.id);
  });


  const handlePass = useMemoizedFn(async () => {
    SubmitModal.show({
      title: '确认无问题，不修改',
      maxLength: 500,
      label: '备注说明',
      onCommit: async ({ reason }: { reason: string }) => {
        await outsourceDemandMoveMove({
          id: detail.id,
          state: detail.state,
          pass: true,
          note: reason,
          form: {}
        });
        Message.success('操作成功');
        loadDetail(detail.id);
      },
    });
  });

  const prefix = `mpc-detail-bottom-bind-execute-data`;

  return (
    // 忽略 TS 错误：TabContent 组件的类型定义可能与当前使用方式不完全匹配
    // @ts-ignore
    <TabContent
      tabKey={tabKey}
      bottom={
        <div className="button-list">
          {
            [undefined, 'NONE'].includes(detail?.sub_state?.toUpperCase()) && (
              <SubmitCaseReportPopover
              showPopover={detail?.product_area !== 'OVERSEAS'}
              >
                <Button
                    icon={<Check />}
                    loading={loading}
                    className="pull-right m-t-8"
                    type="primary"
                    onClick={handleSubmit}
                >
                    提交结案
                </Button>
              </SubmitCaseReportPopover>
            )
          }
          {
            ['REVIEW_PASSED', 'REVIEW_ABNORMAL'].includes(detail?.sub_state?.toUpperCase()) && (
              <Space size={12} className="pull-right m-t-8">
                <Button
                  icon={<RollTopLeft />}
                  loading={loading}
                  danger
                  onClick={handleBack}
                >
                  退回修改
                </Button>
                <Button
                  icon={<Check />}
                  loading={loading}
                  type="primary"
                  onClick={handlePass}
                >
                  确认无问题，不修改
                </Button>
              </Space>
            )
          }
        </div>
      }
      canScroll={true}
      className={`${prefix}`}
    >
      {topSlot}
      <QuotationSummary
           ref={quotationSummaryRef}
           detail={detail}
           planInfo={detail?.project_plan_info}
           mode="xiaohao"
           editType="making"
           readonly={![undefined, 'NONE'].includes(detail?.sub_state?.toUpperCase())}
           onChange={(values) => setSummaryValues(values)}
           components={{
               UploaderMarketingWorks,
               UploadHeatingBoostWorks
           }}
       />
       
      {!!detail?.structured && <NewCooperate
        ref={cooperateRef}
        showBindDataKpi={false}
        hasTitle={false}
        readonly={![undefined, 'NONE'].includes(detail?.sub_state?.toUpperCase())}
        detail={detail}
        planInfo={cooperateData || detail.project_plan_info}
        category_id={detail.l3_category_id}
        onChange={(planInfo: any) => {
          setCooperateData(planInfo);
        }}
      />}
      
      {
        (detail?.product_area === 'OVERSEAS') && <div className='making-statement-attachment'>
          <StatementAttachmentItem
            ref={statementAttachmentRef}
            value={overseasAttachment}
            view={![undefined, 'NONE'].includes(detail?.sub_state?.toUpperCase())}
            onChange={setOverseasAttachment}
            detail={detail}
            isHideInvoice={isHideInvoice}
          />
        </div>
      }
    </TabContent>
  );
};
